import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { Plan, PlanQuote } from "../../types";
import { bynOf } from "../../utils/api";
import { useAppConfig } from "../../config/AppConfigContext";
import { Banner, Icon } from "../common/ui";

/**
 * «Как удобнее оплатить?» — one tariff, two ways to pay, by where the buyer is:
 *
 *   • Belarus & Russia → a bank card through bePaid (Белкарт, МИР, Visa, MC).
 *     Charged in BYN (the ₽ price at the admin's rate); the price reads
 *     «1 000 ₽ (37 BYN)». Automatic when bePaid is on (`bepaidAuto`), else the
 *     plan's own payment link, confirmed by the admin;
 *   • Europe & elsewhere → PayPal or any card without a PayPal account, in $;
 *     upgrade credit and kept renewal prices apply here (PayPal is automatic).
 *
 * The buyer's language decides which option comes first. A plan without a
 * bePaid link still shows the option, with «write to the admin» instead —
 * PayPal does not work from Russia, so that buyer must never hit a dead end.
 */
export default function PayMethodSheet({
  plan, quote, onBepaid, bepaidAuto = false, bepaidSandbox = false, onPaypal, onRequestLink, contact, onClose, accountName,
}: {
  /** Who is paying — shown so a buyer always sees which account gets the access. */
  accountName?: string;
  plan: Plan;
  quote?: PlanQuote;
  /** undefined → no bePaid here (no link, or off). May be async (automatic checkout). */
  onBepaid?: () => void | Promise<void>;
  /** Automatic bePaid checkout (access opens on payment) rather than a link. */
  bepaidAuto?: boolean;
  bepaidSandbox?: boolean;
  onPaypal?: () => void;          // undefined → PayPal is unavailable here
  onRequestLink: () => void;      // no bePaid link for this plan → ask the team for one
  contact: string;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const { config } = useAppConfig();
  const isRu = !i18n.language?.startsWith("en");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const title = (isRu ? plan.title_ru : plan.title_en) || plan.title_ru;
  const rub = Number(plan.price).toLocaleString("ru-RU", { maximumFractionDigits: 0 });
  const usd = Number(quote?.due ?? plan.price_usd).toFixed(Number(quote?.due ?? plan.price_usd) % 1 ? 2 : 0);
  const credit = quote?.kind === "upgrade" && Number(quote.credit) > 0 ? Number(quote.credit) : 0;
  const kept = quote?.kind === "renew" && quote.list_price ? Number(quote.list_price) : 0;
  // bePaid charges BYN; the server's quote (with its own credit / kept price)
  // wins over the list conversion.
  const bq = quote?.bepaid;
  const bynList = bynOf(plan.price, config.byn_per_100_rub);
  const bynDue = bq ? Number(bq.due) : bynList;
  const bynCredit = bq?.kind === "upgrade" && Number(bq.credit) > 0 ? Number(bq.credit) : 0;
  const bynKept = bq?.kind === "renew" && bq.list_price ? Number(bq.list_price) : 0;
  const payBepaid = async () => {
    if (!onBepaid) return;
    setError(false);
    setBusy(true);
    try { await onBepaid(); } catch { setError(true); setBusy(false); }
  };
  const term = plan.days >= 360 ? t("planCard.durYear") : t("planCard.durMonths", { count: Math.round(plan.days / 30) });

  // Both methods take Visa and Mastercard — they differ only in the gateway
  // (and МИР / Белкарт go through «Карта» only), so the brands are spelled out.
  const brands = (key: string) => (
    <div className="method-card__brands">
      {t(key).split(",").map((b) => <span key={b} className="method-brand">{b}</span>)}
    </div>
  );

  const bepaid = (
    <section key="bepaid" className="method-card">
      <header className="method-card__head">
        <span className="method-card__icon"><Icon name="card" size={18} /></span>
        <span>
          <span className="method-card__region">{t("payMethod.byRu")}</span>
          <span className="method-card__how">{t("payMethod.byRuHow")}</span>
        </span>
        <span className="method-card__price">{rub} ₽ <small>({bynList} BYN)</small></span>
      </header>
      {brands("payMethod.byRuBrands")}
      {bepaidAuto && bynCredit > 0 && <p className="method-card__perk"><Icon name="sparkles" size={13} /> {t("payMethod.bynCredit", { credit: bynCredit.toFixed(0), due: bynDue.toFixed(0) })}</p>}
      {bepaidAuto && bynKept > 0 && <p className="method-card__perk"><Icon name="lock" size={13} /> {t("payMethod.bynKept", { due: bynDue.toFixed(0), list: bynKept.toFixed(0) })}</p>}
      {onBepaid ? (
        <>
          <button className="btn btn-primary btn-block" onClick={payBepaid} disabled={busy}>
            {busy ? <span className="spinner" style={{ width: 16, height: 16 }} /> : <Icon name="card" size={16} />} {t("payMethod.byRuPay")}
          </button>
          <p className="method-card__note">{bepaidAuto ? t("payMethod.byRuNoteAuto", { byn: bynDue.toFixed(0) }) : t("payMethod.byRuNote")}</p>
          {bepaidSandbox && <Banner kind="info">{t("payMethod.bepaidSandbox")}</Banner>}
          {error && <Banner kind="error">{t("payMethod.bepaidError")}</Banner>}
        </>
      ) : (
        <>
          <p className="method-card__note">{t("payMethod.byRuNoLink")}</p>
          <button className="btn btn-primary btn-block" onClick={onRequestLink}>
            <Icon name="send" size={16} /> {t("payMethod.requestLink")}
          </button>
          {contact && (
            <button className="btn btn-glass btn-block" onClick={() => window.open(`https://t.me/${contact}`, "_blank", "noopener,noreferrer")}>
              <Icon name="send" size={16} /> {t("planCard.writeAdmin")}
            </button>
          )}
        </>
      )}
    </section>
  );

  const paypal = (
    <section key="paypal" className="method-card">
      <header className="method-card__head">
        <span className="method-card__icon"><Icon name="globe" size={18} /></span>
        <span>
          <span className="method-card__region">{t("payMethod.world")}</span>
          <span className="method-card__how">{t("payMethod.worldHow")}</span>
        </span>
        <span className="method-card__price">${usd}</span>
      </header>
      {brands("payMethod.worldBrands")}
      {credit > 0 && <p className="method-card__perk"><Icon name="sparkles" size={13} /> {t("planCard.creditLine", { credit: credit.toFixed(2) })}</p>}
      {kept > 0 && <p className="method-card__perk"><Icon name="lock" size={13} /> {t("planCard.priceKept", { price: usd, list: kept.toFixed(0) })}</p>}
      {onPaypal ? (
        <button className="btn btn-primary btn-block" onClick={onPaypal}>
          <Icon name="globe" size={16} /> {t("payMethod.worldPay")}
        </button>
      ) : (
        <p className="method-card__note">{t("payMethod.worldUnavailable")}</p>
      )}
    </section>
  );

  return (
    <div className="sheet-backdrop" role="presentation" onClick={onClose}>
      <div className="sheet glass glass-lg" role="dialog" aria-modal="true" aria-labelledby="method-title" onClick={(e) => e.stopPropagation()}>
        <div className="between" style={{ marginBottom: 10 }}>
          <p className="eyebrow" style={{ margin: 0 }}>{t("payMethod.eyebrow")}</p>
          <button className="icon-btn" onClick={onClose} aria-label={t("report.close")} style={{ width: 36, height: 36 }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <h2 className="t-title" id="method-title" style={{ margin: 0 }}>{t("payMethod.title")}</h2>
        <p className="t-sm muted" style={{ margin: "4px 0 4px" }}>{title} · {term}</p>
        {accountName && (
          <p className="t-xs muted-2" style={{ margin: "0 0 14px" }}>
            <Icon name="user" size={12} /> {t("payMethod.forAccount", { name: accountName })}
          </p>
        )}
        <div className="method-list">{isRu ? [bepaid, paypal] : [paypal, bepaid]}</div>
      </div>
    </div>
  );
}
